import { Box, Button, Text, VStack } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react'
import { Link } from 'react-router-dom'

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeUpSlow = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Blue glow pulses - more pronounced (15% larger fluctuations)
const glowPulse1 = keyframes`
  0%, 100% { opacity: 0.5; transform: translateX(-50%) scale(1); }
  50% { opacity: 0.85; transform: translateX(-50%) scale(1.15); }
`

const glowPulse2 = keyframes`
  0%, 100% { opacity: 0.4; transform: translateX(-50%) scale(1.05); }
  50% { opacity: 0.7; transform: translateX(-50%) scale(0.9); }
`

const glowPulse3 = keyframes`
  0%, 100% { opacity: 0.3; transform: translateX(-50%) scale(0.85); }
  50% { opacity: 0.6; transform: translateX(-50%) scale(1.15); }
`

// Dark center pulse
const darkPulse = keyframes`
  0%, 100% { opacity: 0.7; transform: translateX(-50%) scale(1); }
  50% { opacity: 0.9; transform: translateX(-50%) scale(1.1); }
`

export default function Home() {
  return (
    <Box
      position="relative"
      minH="calc(100vh - 57px)"
      bg="gray.50"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
    >
      {/* Blue gradient base at bottom */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        h="30%"
        bg="linear-gradient(to bottom, transparent, rgba(59,130,246,0.4) 40%, rgba(59,130,246,0.8) 80%, rgba(37,99,235,1))"
        pointerEvents="none"
      />

      {/* Glow layer 1 - Primary blue (outer) */}
      <Box
        position="absolute"
        bottom="-60px"
        left="50%"
        transform="translateX(-50%)"
        w="70%"
        h="250px"
        bg="radial-gradient(ellipse at center, rgba(59,130,246,0.7), transparent 70%)"
        animation={`${glowPulse1} 6s ease-in-out infinite`}
        pointerEvents="none"
        filter="blur(25px)"
      />

      {/* Glow layer 2 - Lighter blue */}
      <Box
        position="absolute"
        bottom="-40px"
        left="50%"
        transform="translateX(-50%)"
        w="55%"
        h="200px"
        bg="radial-gradient(ellipse at center, rgba(96,165,250,0.6), transparent 70%)"
        animation={`${glowPulse2} 8s ease-in-out infinite`}
        pointerEvents="none"
        filter="blur(30px)"
        style={{ animationDelay: '2s' }}
      />

      {/* Glow layer 3 - Cyan accent */}
      <Box
        position="absolute"
        bottom="-30px"
        left="50%"
        transform="translateX(-50%)"
        w="45%"
        h="180px"
        bg="radial-gradient(ellipse at center, rgba(34,211,238,0.5), transparent 70%)"
        animation={`${glowPulse3} 10s ease-in-out infinite`}
        pointerEvents="none"
        filter="blur(20px)"
        style={{ animationDelay: '4s' }}
      />

      {/* Dark center circle */}
      <Box
        position="absolute"
        bottom="-20px"
        left="50%"
        transform="translateX(-50%)"
        w="25%"
        h="120px"
        bg="radial-gradient(ellipse at center, rgba(0,0,0,0.9), rgba(0,0,0,0.5) 50%, transparent 70%)"
        animation={`${darkPulse} 7s ease-in-out infinite`}
        pointerEvents="none"
        filter="blur(10px)"
        style={{ animationDelay: '1s' }}
      />

      {/* Content */}
      <SignedOut>
        <VStack gap={6}>
          <Text
            fontSize="lg"
            color="gray.600"
            fontWeight="medium"
            animation={`${fadeUpSlow} 1.2s ease-out forwards`}
            style={{ animationDelay: '0.2s' }}
            opacity={0}
          >
            Welcome to Dominus Alpha v0.1
          </Text>
          <SignInButton mode="modal">
            <Button
              colorScheme="blue"
              size="lg"
              px={12}
              animation={`${fadeUp} 1s ease-out forwards`}
              style={{ animationDelay: '0.5s' }}
              opacity={0}
            >
              Sign In
            </Button>
          </SignInButton>
        </VStack>
      </SignedOut>

      <SignedIn>
        <VStack gap={6}>
          <Text
            fontSize="lg"
            color="gray.600"
            fontWeight="medium"
            animation={`${fadeUpSlow} 1.2s ease-out forwards`}
            style={{ animationDelay: '0.2s' }}
            opacity={0}
          >
            Welcome to Dominus Alpha v0.1
          </Text>
          <Link to="/chat">
            <Button
              colorScheme="blue"
              size="lg"
              px={12}
              animation={`${fadeUp} 1s ease-out forwards`}
              style={{ animationDelay: '0.5s' }}
              opacity={0}
            >
              Start Chatting
            </Button>
          </Link>
        </VStack>
      </SignedIn>
    </Box>
  )
}
